import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import i18n from '../i18n';
import { SITE } from '../siteConfig';

export interface RouteMetaInfo {
  /** Short page name (announced on navigation). */
  title: string;
  /** Full <title> text. */
  documentTitle: string;
  description: string;
  /** Public pages are indexable; signed-in app pages are not. */
  index: boolean;
  canonicalPath: string;
}

const PUBLIC_PAGES: Record<string, string> = {
  '/how-it-works': 'howItWorks',
  '/pricing': 'pricing',
  '/editorial-standards': 'editorial',
  '/privacy': 'privacy',
  '/terms': 'terms',
  '/support': 'support',
  '/sample': 'sample',
};

const APP_PAGES: Record<string, string> = {
  '/home': 'nav.home',
  '/practices': 'nav.practices',
  '/calendar': 'nav.calendar',
  '/library': 'nav.library',
  '/journal': 'nav.journal',
  '/leaderboard': 'nav.leaderboard',
  '/settings': 'nav.settings',
  '/admin': 'nav.admin',
  '/onboarding': 'meta.titles.onboarding',
};

/** Review fix F11: per-route title/description/canonical in one place (covers every route,
 * including pages this pass doesn't otherwise touch). Pure -- also used by RouteAnnouncer. */
export function getRouteMeta(pathname: string): RouteMetaInfo {
  const t = (key: string) => i18n.t(key);
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const defaultDescription = t('meta.defaultDescription');
  const withSite = (title: string) => `${title} · ${SITE.name}`;

  if (path === '/' || path === '/welcome') {
    const title = t('meta.titles.landing');
    return { title, documentTitle: title, description: defaultDescription, index: true, canonicalPath: '/' };
  }
  const publicKey = PUBLIC_PAGES[path];
  if (publicKey) {
    const title = t(`meta.titles.${publicKey}`);
    return {
      title,
      documentTitle: withSite(title),
      description: t(`meta.descriptions.${publicKey}`),
      index: true,
      canonicalPath: path,
    };
  }
  if (path === '/login' || path === '/signup') {
    const title = t(path === '/login' ? 'meta.titles.signin' : 'meta.titles.signup');
    return { title, documentTitle: withSite(title), description: defaultDescription, index: true, canonicalPath: path };
  }
  const appKey = path.startsWith('/practice/') ? 'meta.titles.practice' : APP_PAGES[path];
  const title = appKey ? t(appKey) : SITE.name;
  return { title, documentTitle: appKey ? withSite(title) : title, description: defaultDescription, index: false, canonicalPath: '/' };
}

function setMeta(attribute: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attribute, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/** Keeps <title>, description, canonical, robots and the social-preview tags in step with the
 * current route and language. Crawlers that don't run JavaScript still only see index.html's
 * static tags and <noscript> summary -- true per-page crawlable HTML needs pre-rendering. */
export function RouteMeta() {
  const { pathname } = useLocation();
  const { i18n: instance } = useTranslation();

  useEffect(() => {
    const meta = getRouteMeta(pathname);
    const url = `${SITE.canonicalOrigin}${meta.canonicalPath}`;
    document.title = meta.documentTitle;
    setMeta('name', 'description', meta.description);
    setMeta('name', 'robots', meta.index ? 'index,follow' : 'noindex');
    setMeta('property', 'og:title', meta.documentTitle);
    setMeta('property', 'og:description', meta.description);
    setMeta('property', 'og:url', url);
    setMeta('name', 'twitter:title', meta.documentTitle);
    setMeta('name', 'twitter:description', meta.description);
    setCanonical(url);
  }, [pathname, instance.language]);

  return null;
}
